import React, { useEffect, useMemo, useCallback } from "react"; // Added useCallback
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import ColumnGroupingTable from "../../../table/ColumnGroupingTable.component";
import { Typography } from "@mui/material";
import { useHedgeRatioContext } from "../../../../context/HedgeRatioContext";
import { applyFigureFormatting } from "../../../../utils/utils";

// --- Constants (Keep outside) ---
const INFLOW = "Inflow";
const TOTAL_INFLOW = "Total Inflow";
const OUTFLOW = "Outflow";
const TOTAL_OUTFLOW = "Total Outflow";
const NET_CASHFLOW = "Net Cashflow";
const CUMULATIVE_CASHFLOW = "Cumulative Cashflow";
const COMMITTED_BANK_FACILITY_TOTAL = "Committed Bank Facility - Total";
const TOTAL_LIQUIDITY_INVESTMENT_AND_SLRM = "Total Liquidity Investment + SLRM";
const CONTINGENT_LIQUIDITY = "Contingent Source of Liquidity";
const NET_LIQUIDITY_POSITION = "Net Liquidity Position";

const FINAL_ROW_ORDER = [
  INFLOW,
  TOTAL_INFLOW,
  OUTFLOW,
  TOTAL_OUTFLOW,
  NET_CASHFLOW,
  CUMULATIVE_CASHFLOW,
  COMMITTED_BANK_FACILITY_TOTAL,
  TOTAL_LIQUIDITY_INVESTMENT_AND_SLRM,
  CONTINGENT_LIQUIDITY,
  NET_LIQUIDITY_POSITION,
];

const calculate12MonthsTotal = (datas) => {
  return Object.entries(datas).reduce((sum, [key, value]) => {
    if (key.match(/^[A-Za-z]{3}-\d{2}$/)) {
      return sum + Number(value);
    }
    return sum;
  }, 0);
};

function createTotalRowData({ type } = {}) {
  const totalDatas = {};

  type?.info?.forEach((info) => {
    const datas = info?.datas || {};

    Object.entries(datas).forEach(([key, value]) => {
      totalDatas[key] = (totalDatas[key] || 0) + value;
    });
  });

  const total12Months = calculate12MonthsTotal(totalDatas);

  return {
    type: type?.type,
    cashflow: `Total ${type?.type}`,
    ...totalDatas,
    "12 months": total12Months,
    cellStyles: {
      fontWeight: "bold",
      backgroundColor: "#F5F5F5",
      borderTop: "1px solid #000000",
      borderBottom: "1px solid #000000",
    },
  };
}

function createInfoRowData({ info, type, index, includeTotalRow } = {}) {
  const dynamicData =
    info?.datas && typeof info.datas === "object" ? info.datas : {};

  const detail12Months = includeTotalRow
    ? calculate12MonthsTotal(dynamicData)
    : undefined;

  const row = {
    type: type?.type,
    cashflow: info?.name,
    ...dynamicData,
  };

  if (detail12Months !== undefined) {
    row["12 months"] = detail12Months;
  }

  return row;
}

function calculateNetCashflowRow(totalRows) {
  const netCashflow = {};

  const totalInflow = totalRows.find((row) => row.cashflow === TOTAL_INFLOW);
  const totalOutflow = totalRows.find((row) => row.cashflow === TOTAL_OUTFLOW);

  if (!totalInflow || !totalOutflow) {
    return {
      type: NET_CASHFLOW,
      cashflow: "Net - Cashflow",
    };
  }

  const allKeys = new Set([
    ...Object.keys(totalInflow),
    ...Object.keys(totalOutflow),
  ]);

  const metaKeys = new Set(["type", "cashflow", "cellStyles", "12 months"]);

  allKeys.forEach((key) => {
    if (!metaKeys.has(key)) {
      const inflowValue = Number(totalInflow[key]) || 0;
      const outflowValue = Number(totalOutflow[key]) || 0;
      netCashflow[key] = inflowValue + outflowValue;
    }
  });

  return {
    type: NET_CASHFLOW,
    cashflow: "Net Cash-flow",
    ...netCashflow,
    cellStyles: {
      borderBottom: "1px solid #000000",
    },
  };
}

function calculateCumulativeCashflowRow(netRow) {
  const cumulativeCashflow = {};
  let runningTotal = 0;

  const dataKeys = Object.keys(netRow).filter((key) => {
    const metaKeys = new Set(["type", "cashflow", "cellStyles", "12 months"]);
    return !metaKeys.has(key);
  });

  dataKeys.forEach((key) => {
    const netValue = Number(netRow[key]) || 0;
    runningTotal += netValue;
    cumulativeCashflow[key] = runningTotal;
  });

  return {
    type: CUMULATIVE_CASHFLOW,
    cashflow: "Cumulative Cash-flow (A)",
    ...cumulativeCashflow,
    cellStyles: {
      borderBottom: "1px solid #000000",
    },
  };
}

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const CashflowTable = ({
  mainResponse,
  creditFacilityTableDatas,
  setLiquidityModelTableColumns,
  setLiquidityModelTableDatas,
} = {}) => {
  const { selectedFigureDivisor, selectedDecimalsIn } = useHedgeRatioContext();

  const rawCommittedBankFacilityTotalValue = useMemo(() => {
    const totalFromUpstream = creditFacilityTableDatas.reduce((total, row) => {
      switch (row.creditFacility) {
        case "Committed Group Loan Fcty (as a Lender) [Deduction]":
        case "Committed Conduit Facility (TRAC)":
          return total + Number(row.facility);

        case "Committed Bilateral BK Fcty":
        case "Committed Syndicated BK Fcty":
          return total + Number(row.unUtilized);

        default:
          return total;
      }
    }, 0);
    return totalFromUpstream * (selectedFigureDivisor || 1);
  }, [creditFacilityTableDatas, selectedFigureDivisor]);

  const rawTotalFacilitiesUtilized = useMemo(() => {
    const totalFromUpstream = creditFacilityTableDatas.reduce((total, row) => {
      switch (row.creditFacility) {
        case "Total Facilities":
          return total + Number(row.utilized);
        default:
          return total;
      }
    }, 0);
    return totalFromUpstream * (selectedFigureDivisor || 1);
  }, [creditFacilityTableDatas, selectedFigureDivisor]);

  const columns = useMemo(() => {
    const dynamicColumns =
      mainResponse &&
      mainResponse?.length > 0 &&
      mainResponse?.[0]?.info?.[0]?.datas
        ? Object.keys(mainResponse?.[0]?.info?.[0]?.datas)?.map((key) => {
            return {
              id: key,
              label: key,
              minWidth: 80,
              align: "right",
            };
          })
        : [];

    if (dynamicColumns.length > 0) {
      dynamicColumns.push({
        id: "12 months",
        label: "12 months",
        minWidth: 65,
        align: "right",
      });
    }

    return [
      { id: "type", label: "Type", minWidth: 120, align: "left" },
      { id: "cashflow", label: "Cashflow", minWidth: 200, align: "left" },
      ...dynamicColumns,
    ];
  }, [mainResponse]);

  const flattenLiquidityModelData = useCallback(
    (data) => {
      const rowMap = new Map();
      const totalInflowOutflowRows = [];
      let cumulativeRow = null;

      const totalLiquidityRow = data?.find(
        (type) => type?.type === TOTAL_LIQUIDITY_INVESTMENT_AND_SLRM,
      );
      const totalLiquidityDatas = totalLiquidityRow?.info?.[0]?.datas || {};

      data?.forEach((type) => {
        const isCalculatedType = [INFLOW, OUTFLOW].includes(type?.type);

        const isNonCashflowType = [
          TOTAL_LIQUIDITY_INVESTMENT_AND_SLRM,
        ].includes(type?.type);

        const detailRows =
          type?.info?.map((info, index) =>
            createInfoRowData({
              info,
              type,
              index,
              includeTotalRow: isCalculatedType,
            }),
          ) || [];

        if (isNonCashflowType) {
          detailRows.forEach((row) => {
            row["12 months"] = "";
            row["cashflow"] = TOTAL_LIQUIDITY_INVESTMENT_AND_SLRM + " (C)";
          });
        }

        const primaryKey = detailRows[0]?.type || type.type;
        if (primaryKey) {
          rowMap.set(primaryKey, detailRows);
        }

        if (isCalculatedType) {
          const totalRow = createTotalRowData({ type });
          totalInflowOutflowRows.push(totalRow);
          rowMap.set(`Total ${type.type}`, [totalRow]);
        }
      });

      let netRow = null;
      if (totalInflowOutflowRows.length === 2) {
        netRow = calculateNetCashflowRow(totalInflowOutflowRows);
        netRow["12 months"] = calculate12MonthsTotal(netRow);
        rowMap.set(NET_CASHFLOW, [netRow]);
      }

      if (netRow) {
        cumulativeRow = calculateCumulativeCashflowRow(netRow);
        const lastMonthColumnIndex = columns.length - 2;
        const lastMonthKey = columns[lastMonthColumnIndex]?.id;

        if (lastMonthKey) {
          cumulativeRow["12 months"] = cumulativeRow[lastMonthKey];
        } else {
          cumulativeRow["12 months"] = 0;
        }
        rowMap.set(CUMULATIVE_CASHFLOW, [cumulativeRow]);
      }

      const monthlyKeys = columns
        .filter(
          (col) =>
            col.id !== "12 months" &&
            col.id !== "type" &&
            col.id !== "cashflow",
        )
        .map((col) => col.id);

      const committedBankData = monthlyKeys.reduce((acc, key) => {
        acc[key] = rawCommittedBankFacilityTotalValue;
        return acc;
      }, {});

      const committedBankRow = {
        type: COMMITTED_BANK_FACILITY_TOTAL,
        cashflow: COMMITTED_BANK_FACILITY_TOTAL + " (B)",
        ...committedBankData,
        "12 months": "",
        cellStyles: {
          borderBottom: "1px solid #000000",
        },
      };

      rowMap.set(COMMITTED_BANK_FACILITY_TOTAL, [committedBankRow]);

      const contingentData = monthlyKeys.reduce((acc, key) => {
        const committedValue = Number(rawCommittedBankFacilityTotalValue) || 0;
        const liquidityValue = Number(totalLiquidityDatas[key]) || 0;
        const utilizedValue = Number(rawTotalFacilitiesUtilized) || 0;

        acc[key] = committedValue + liquidityValue - utilizedValue;
        return acc;
      }, {});

      const contingentRow = {
        type: CONTINGENT_LIQUIDITY,
        cashflow: CONTINGENT_LIQUIDITY + " (D)",
        ...contingentData,
        "12 months": "",
        cellStyles: {
          borderTop: "1px solid #000000",
          borderBottom: "1px solid #000000",
        },
      };

      rowMap.set(CONTINGENT_LIQUIDITY, [contingentRow]);

      const cumulativeCashflowArray = rowMap.get(CUMULATIVE_CASHFLOW);
      const cumulativeCashflowData = cumulativeCashflowArray
        ? cumulativeCashflowArray[0]
        : {};

      const contingentLiquidityArray = rowMap.get(CONTINGENT_LIQUIDITY);
      const contingentLiquidityData = contingentLiquidityArray
        ? contingentLiquidityArray[0]
        : {};

      const netLiquidityData = monthlyKeys.reduce((acc, key) => {
        const cumulativeValue = Number(cumulativeCashflowData[key]) || 0;
        const contingentValue = Number(contingentLiquidityData[key]) || 0;

        acc[key] = cumulativeValue + contingentValue;
        return acc;
      }, {});

      const netLiquidityRow = {
        type: NET_LIQUIDITY_POSITION,
        cashflow: NET_LIQUIDITY_POSITION + " (E=A+D)",
        ...netLiquidityData,
        "12 months": "",
        cellStyles: {
          borderBottom: "1px solid #000000",
        },
      };

      if (cumulativeCashflowArray && contingentLiquidityArray) {
        rowMap.set(NET_LIQUIDITY_POSITION, [netLiquidityRow]);
      }

      let finalRows = [];

      FINAL_ROW_ORDER.forEach((key) => {
        if (rowMap.has(key)) {
          finalRows.push(...rowMap.get(key));
        }
      });

      // 💡 NEW STEP: Apply Formatting to all data values in finalRows
      const formattedRows = finalRows.map((row) => {
        const formattedRow = {};
        for (const key in row) {
          if (
            key !== "type" &&
            key !== "cashflow" &&
            key !== "cellStyles" &&
            key !== "12 months" && // 12 months column needs formatting too
            typeof row[key] !== "object"
          ) {
            // Apply formatting to monthly data
            formattedRow[key] = applyFigureFormatting(
              row[key],
              selectedFigureDivisor,
              selectedDecimalsIn,
            );
          } else if (key === "12 months" && row[key] !== "") {
            // Apply formatting to 12 months column if it has a value
            formattedRow[key] = applyFigureFormatting(
              row[key],
              selectedFigureDivisor,
              selectedDecimalsIn,
            );
          } else {
            formattedRow[key] = row[key];
          }
        }
        return formattedRow;
      });

      return formattedRows;
    },
    [
      columns,
      rawCommittedBankFacilityTotalValue,
      rawTotalFacilitiesUtilized,
      selectedFigureDivisor,
      selectedDecimalsIn,
    ],
  );

  const datas = useMemo(() => {
    return flattenLiquidityModelData(mainResponse);
  }, [mainResponse, flattenLiquidityModelData]);

  useEffect(() => {
    if (datas && datas?.length) {
      setLiquidityModelTableColumns(columns);
      setLiquidityModelTableDatas(datas);
    }
  }, [
    columns,
    datas,
    setLiquidityModelTableColumns,
    setLiquidityModelTableDatas,
  ]);

  return (
    <Grid container spacing={2} sx={{ marginBottom: 2 }}>
      <Grid size={{ xs: 12, md: 12, lg: 12 }}>
        {!mainResponse ? (
          <Item>
            <Typography
              variant="p"
              component="p"
              sx={{
                color: "#adadad",
                minHeight: "250px",
              }}
            >
              There was an error in fetching the data. Please try again later.
            </Typography>
          </Item>
        ) : !mainResponse?.length ? (
          <Item>
            <Typography
              variant="p"
              component="p"
              sx={{
                color: "#adadad",
                minHeight: "250px",
              }}
            >
              No data available for the selected filters.
            </Typography>
          </Item>
        ) : (
          // Cashflow
          <ColumnGroupingTable
            columns={columns}
            datas={datas}
            loading={false}
            disablePagination={true}
            applyThousandsSeparator={true}
            tableHeight={940}
            rowSpanKey="type" // To merge and center vertically
          />
        )}
      </Grid>
    </Grid>
  );
};

export default CashflowTable;
